import React from 'react'
import { isNil } from 'lodash'
import { cn } from '@/lib/utils'
import Image from '@/components/Image'

type Props = {
  src?: string | null
  name?: string | null
  initials?: string
  size?: number
  online?: boolean
  className?: string
  /** `soft` = #BECEEF placeholder (contacts). `brand` = purple gradient (current user). `company` = rounded square. */
  tone?: 'soft' | 'brand' | 'company'
  shape?: 'circle' | 'square'
}

const getInitials = (name?: string | null) =>
  (name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('')

/** Figma avatar: 36px #BECEEF circle; online = 9px #2BB23B dot with white stroke bottom-right. */
const Avatar = ({
  src,
  name,
  initials,
  size = 36,
  online,
  className,
  tone = 'soft',
  shape = 'circle',
}: Props) => {
  const text = initials ?? getInitials(name)
  const radius = shape === 'square' ? 'rounded-[6px]' : 'rounded-full'
  const fontSize = Math.max(10, Math.round(size * 0.36))

  return (
    <span
      className={cn('relative inline-flex shrink-0 select-none', className)}
      style={{ width: size, height: size }}
    >
      {!isNil(src) && src !== '' ? (
        <Image
          className={cn('h-full w-full object-cover', radius)}
          src={src}
          width={size * 2}
          height={size * 2}
          alt={name ?? text ?? 'avatar'}
        />
      ) : (
        <span
          className={cn(
            'flex h-full w-full items-center justify-center font-medium leading-none tracking-[-0.2px]',
            radius,
            tone === 'soft' && 'bg-avatar text-ink-2/80 dark:text-ink',
            tone === 'brand' &&
              'bg-violet text-white shadow-[0_0_0_1px_rgba(77,0,255,0.12),2px_4px_12px_0_rgba(95,34,238,0.12),inset_0_0_2px_2px_rgba(255,255,255,0.22)] border border-[rgba(77,0,255,0.3)]',
            tone === 'company' && 'bg-violet-2 text-white'
          )}
          style={{ fontSize }}
        >
          {tone === 'brand' ? <span className="lc-text-glass">{text}</span> : text}
        </span>
      )}
      {online && (
        <span
          className="absolute rounded-full bg-success ring-[1.5px] ring-surface"
          style={{ width: size * 0.25, height: size * 0.25, right: -1, bottom: -1 }}
        />
      )}
    </span>
  )
}

export default Avatar
